<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import ChevronLeft from "../utils/assets/chevron_left_icon.svelte";
  import ChevronRight from "../utils/assets/chevron_right_icon.svelte";

  let container: HTMLDivElement;
  let slideCount = 0;
  let currentIndex = 0;
  let startX = 0;
  let currentX = 0;
  let isDragging = false;
  let startY = 0;
  let currentY = 0;
  let isHorizontalSwipe = false;
  const swipeDetectionThreshold = 40;

  let containerWidth = 0;
  let slideWidth = 0;
  const gap = 12;
  const maxVisibleDots = 5;
  let visibleDots: number[] = [];

  $: offset =
    currentIndex === 0
      ? 0
      : currentIndex * (slideWidth + gap) - (containerWidth - slideWidth) / 2;

  $: {
    let start = 0;
    if (slideCount > maxVisibleDots) {
      start = Math.max(
        0,
        Math.min(
          currentIndex - Math.floor(maxVisibleDots / 2),
          slideCount - maxVisibleDots
        )
      );
    }
    visibleDots = Array.from(
      { length: Math.min(slideCount, maxVisibleDots) },
      (_, i) => start + i
    );
  }

  onMount(() => {
    slideCount = container.children.length;
    containerWidth = container.clientWidth;
    if (slideCount > 0) {
      slideWidth = (container.children[0] as HTMLElement).offsetWidth;
    }

    const handleTouchStart = (event: TouchEvent) => {
      isDragging = true;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      isHorizontalSwipe = false;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isDragging) return;

      currentX = event.touches[0].clientX;
      currentY = event.touches[0].clientY;

      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);

      if (
        !isHorizontalSwipe &&
        deltaX > swipeDetectionThreshold &&
        deltaX > deltaY * 3
      ) {
        isHorizontalSwipe = true;
      }

      if (isHorizontalSwipe) {
        event.preventDefault();
        const moveDeltaX = currentX - startX;
        container.style.transform = `translateX(${-offset + moveDeltaX}px)`;
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!isDragging) return;
      isDragging = false;

      if (isHorizontalSwipe) {
        const deltaX = currentX - startX;
        const threshold = 100;

        if (deltaX > threshold) {
          if (currentIndex === 0) {
            currentIndex = slideCount - 1;
          } else {
            currentIndex = currentIndex - 1;
          }
        } else if (deltaX < -threshold) {
          if (currentIndex === slideCount - 1) {
            currentIndex = 0;
          } else {
            currentIndex = currentIndex + 1;
          }
        }

        container.style.transform = `translateX(-${offset}px)`;
      }

      isHorizontalSwipe = false;
    };

    if (container) {
      container.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      container.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      container.addEventListener("touchend", handleTouchEnd, {
        passive: true,
      });
    }

    return () => {
      if (container) {
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
      }
    };
  });

  function goToSlide(index: number): void {
    currentIndex = index;
  }
</script>

<div class="verbaflo-carousel-container">
  <div
    bind:this={container}
    class="verbaflo-carousel-slides"
    style:--offset={`${offset}px`}
  >
    <slot></slot>
  </div>

  <div class="verbaflo-carousel-dots-container">
    <div class="verbaflo-carousel-dots-wrapper">
      {#each visibleDots as dot, i}
        <button
          on:click={() => goToSlide(dot)}
          class="verbaflo-carousel-dot-btn"
        >
          <span
            class="verbaflo-carousel-dot {dot === currentIndex
              ? 'verbaflo-carousel-dot-active'
              : i === 0 || i === visibleDots.length - 1
                ? 'verbaflo-carousel-dot-edge'
                : 'verbaflo-carousel-dot-middle'}"
          ></span>
        </button>
      {/each}
    </div>
    <div class="verbaflo-carousel-counter">
      <div>
        <p>
          {currentIndex + 1} of {slideCount}
        </p>
      </div>
      <div class="verbaflo-nav-btn-container">
        <button
          class="verbaflo-nav-btn"
          on:click={() => {
            currentIndex =
              currentIndex === 0 ? slideCount - 1 : currentIndex - 1;
          }}
        >
          <ChevronLeft size="12" color="black" />
        </button>
        <button
          class="verbaflo-nav-btn"
          on:click={() => {
            currentIndex =
              currentIndex === slideCount - 1 ? 0 : currentIndex + 1;
          }}
        >
          <ChevronRight />
        </button>
      </div>
    </div>
  </div>
</div>

<style>
  :global(#vfwidget .verbaflo-nav-btn-container) {
    display: flex !important;
    align-items: center !important;
    margin-left: 10px !important;
    column-gap: 12px !important;
  }

  :global(#vfwidget .verbaflo-nav-btn) {
    background: none !important;
    border: none !important;
    padding: 0 !important;
    cursor: pointer !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-transform: none !important;
    transition: none !important;
  }

  :global(#vfwidget .verbaflo-nav-btn:hover),
  :global(#vfwidget .verbaflo-nav-btn:active) {
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-carousel-container) {
    position: relative !important;
    width: 100% !important;
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-carousel-slides) {
    display: flex !important;
    gap: 12px !important;
    transition: transform 500ms !important;
    position: relative !important;
    isolation: isolate !important;
    -webkit-transform: translateZ(0) translateX(calc(-1 * var(--offset, 0))) !important;
    transform: translateZ(0) translateX(calc(-1 * var(--offset, 0))) !important;
    -webkit-backface-visibility: hidden !important;
    backface-visibility: hidden !important;
    height: fit-content !important;
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-carousel-dots-container) {
    position: relative !important;
    margin-top: 16px !important;
  }

  :global(#vfwidget .verbaflo-carousel-dots-wrapper) {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  :global(#vfwidget .verbaflo-carousel-dot-btn) {
    margin: 0 4px !important;
    border-radius: 100% !important;
    outline: none !important;
    border: none !important;
    background: none !important;
    padding: 0 !important;
    transition: none !important;
  }

  :global(#vfwidget .verbaflo-carousel-dot-btn:hover),
  :global(#vfwidget .verbaflo-carousel-dot-btn:active) {
    opacity: 1 !important;
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-carousel-dot) {
    display: inline-block !important;
    border-radius: 100% !important;
  }

  :global(#vfwidget .verbaflo-carousel-dot-active) {
    width: 5px !important;
    height: 5px !important;
    background-color: black !important;
  }

  :global(#vfwidget .verbaflo-carousel-dot-edge) {
    width: 3px !important;
    height: 3px !important;
    background-color: #939393 !important;
  }

  :global(#vfwidget .verbaflo-carousel-dot-middle) {
    width: 4px !important;
    height: 4px !important;
    background-color: #939393 !important;
  }

  :global(#vfwidget .verbaflo-carousel-counter) {
    position: absolute !important;
    right: 0 !important;
    top: 50% !important;
    display: flex !important;
    align-items: center !important;
    transform: translateY(-50%) !important;
    font-size: 12px !important;
    color: #4b5563 !important;
  }

  :global(#vfwidget .verbaflo-carousel-counter *) {
    margin: 0 !important;
    font-size: 12px !important;
    color: #4b5563 !important;
    font-weight: 400 !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-property-carousel-container) {
    margin-top: 4px !important;
    background-color: #f6f6f6 !important;
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.1),
      0 1px 2px rgba(0, 0, 0, 0.06) !important;
    border-radius: 8px !important;
    padding: 10px !important;
    max-width: 100% !important;
    overflow: hidden !important;
    position: relative !important;
    isolation: isolate !important;
    transform: translateZ(0) !important;
    backface-visibility: hidden !important;
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-property-card) {
    background-color: #ffffff !important;
    width: 100% !important;
    flex-shrink: 0 !important;
    max-width: 80% !important;
    display: flex !important;
    flex-direction: column !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 8px !important;
    user-select: none !important;
    transform: translateZ(0) !important;
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-property-card.single-card) {
    max-width: 100% !important;
  }

  :global(#vfwidget .verbaflo-property-card-image-container) {
    height: 150px !important;
    width: 100% !important;
    overflow: hidden !important;
    flex-shrink: 0 !important;
    position: relative !important;
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-property-card-image-container::after) {
    content: "" !important;
    position: absolute !important;
    inset: 0 !important;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 33.97%,
      rgba(0, 0, 0, 0.7) 100%
    ) !important;
    pointer-events: none !important;
    touch-action: none !important;
  }

  :global(#vfwidget .verbaflo-property-card-image) {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-top-left-radius: 8px !important;
    border-top-right-radius: 8px !important;
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-property-card-details) {
    padding: 8px !important;
    display: flex !important;
    flex-direction: column !important;
    font-size: 14px !important;
    height: 100% !important;
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-property-card) {
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }

  :global(#vfwidget .verbaflo-carousel-container *),
  :global(#vfwidget .verbaflo-property-carousel-container *) {
    touch-action: pan-y !important;
    pointer-events: auto !important;
  }
</style>
